import { createApiClient, type ApiClient } from '@wyn/contracts';
import { createAuth, type Auth, type AuthConfig } from './auth.ts';
import { deviceId } from './ids.ts';
import { initRealtime } from './realtime.ts';

export interface RuntimeConfig {
  stage: string;
  apiUrl: string;
  /** The WebSocket API's stage URL. Locally the API server takes sockets on /ws, through the Vite proxy. */
  socketUrl?: string;
  auth: AuthConfig;
  /** PostHog's public project key and host; absent or null on a stage without analytics. */
  posthog?: { key: string; host: string } | null;
}

export interface Runtime {
  config: RuntimeConfig;
  auth: Auth;
  api: ApiClient;
}

let runtime: Runtime | null = null;

/** Local dev talks to the docker compose API through the Vite proxy; deployed stages read /config.json. */
export async function initRuntime(): Promise<Runtime> {
  const config: RuntimeConfig = import.meta.env.DEV
    ? { stage: 'local', apiUrl: '/api', auth: { mode: 'dev' }, posthog: import.meta.env.VITE_POSTHOG_KEY ? { key: import.meta.env.VITE_POSTHOG_KEY, host: 'https://eu.i.posthog.com' } : null }
    : ((await (await fetch('/config.json', { cache: 'no-store' })).json()) as RuntimeConfig);
  const auth = createAuth(config.auth);
  runtime = { config, auth, api: createApiClient({ baseUrl: config.apiUrl, getToken: () => auth.getToken(), headers: () => ({ 'x-device-id': deviceId() }) }) };
  // A socket cannot carry an authorization header, so the token rides in the query string.
  const socketBase = config.socketUrl ?? `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${config.apiUrl}/ws`;
  initRealtime(async () => {
    const token = await auth.getToken();
    return token ? `${socketBase}?token=${encodeURIComponent(token)}` : null;
  });
  return runtime;
}

export function rt(): Runtime {
  if (!runtime) throw new Error('runtime not initialised');
  return runtime;
}
